import React, { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  Switch,
  Tab,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";
import Div from "@jumbo/shared/Div/Div";
import JumboTextField from "@jumbo/components/JumboFormik/JumboTextField";
import { Field, useFormikContext } from "formik";
import { Label } from "app/pages/presentation/module/form/types";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import ClearIcon from "@mui/icons-material/Clear";
import { useJumboDialog } from "@jumbo/components/JumboDialog/hooks/useJumboDialog";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import JumboListNoDataPlaceholder from "@jumbo/components/JumboList/components/JumboListNoDataPlaceHolder/JumboListNoDataPlaceholder";
import * as Yup from "yup";
import {
  DSOK_DEVICE_SERVER_PORT_START,
  DSOK_FE_DOMAIN,
  DSOK_POS_SUPPLIERS,
} from "app/utils/constants/settings";
import { useSnackbar } from "notistack";
import { replaceAll } from "app/utils/stringHelpers";
import { getDeviceStatus as getPartnerDeviceStatus } from "app/services/api/partner";
import { getDeviceStatus } from "app/services/api/management";
import GeneralSection from "./sections/GeneralSection";
import PosSection from "./sections/PosSection";
import EkasaSection from "./sections/EkasaSection";
import ExternalDeviceSection from "./sections/ExternalDeviceSection";

const FIELD_NAME = "DEVICES_FIELD";

function generateUuidV4() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export const getDevicesFieldSchema = (devices) => {
  const schema = {};
  const deviceKeys = Object.keys(devices || {});
  if (!deviceKeys?.length) {
    return schema;
  }
  deviceKeys.forEach((device) => {
    schema[device] = {
      uuid: "",
      currency: "",
      testMode: "",
      maintenance: "",
      forceMaintenance: "",
      pos: {
        supplier: "",
        ip: "",
        useHTTPS: "",
        localPort: "",
        serverPort: "",
        username: "",
        password: "",
        printReceipt: "",
        poiId: "",
        serialNumber: "",
        acquirerId: "",
        // Global Payments (len pre supplier "globalPayments")
        currency: "EUR", // mena terminálu (EUR | CZK), default EUR
        tipEnabled: false, // pýtať sa na sprepitné na termináli (config bit 0x20)
      },
      ekasa: {
        ip: "",
        useHTTPS: "",
        localPort: "",
        serverPort: "",
        headerText: "",
        footerText: "",
        dic: "",
        cashRegisterCode: "",
        customer: {
          id: "",
          type: "",
        },
        print: {
          options: {
            to: "",
          },
          printerName: "",
        },
        transactionType: "",
        eKasaType: "none",
        // Česká EET (eKasaType "eet_cz") – priama serverová evidencia na FS.
        eet: {
          eicPopl: "", // EIČ poplatníka (CZ...), NIE DIČ
          eicPoverujiciho: "", // EIČ poverujúceho (voliteľné)
          povereniVicePopl: false,
          idJednotky: "", // ID prevádzkarne
          idPokl: "", // ID pokladne (max 20 znakov)
          rezim: "produkcni", // produkcni | overeni (overovací režim)
          prostredi: "playground", // playground | produkcni
          certRef: "", // odkaz na uložený pokladničný certifikát
        },
      },
      generalServer: {
        enableGeneralServer: "",
        localPort: "",
        serverPort: "",
      },
      connectionCode: "",
      deviceType: "kiosk",
      workspaceId: "",
      location: "",
      orderProcess: {
        enablePosPayment: "",
        enableCashMachine: "",
        enablePrintReceipt: "",
      },
    };
  });
  return schema;
};

export const getDevicesFieldValidationSchema = (t) => ({
  uuid: Yup.string().required(t("messages.checkRequiredFields")),
  currency: Yup.string().required(t("messages.checkRequiredFields")),
  pos: Yup.object().shape({
    supplier: Yup.string().required(t("messages.checkRequiredFields")),
    ip: Yup.string().when("supplier", {
      is: (supplier) =>
        supplier === "dotypay" || supplier === "globalPayments",
      then: (schema) => schema.notRequired(),
      otherwise: (schema) =>
        schema.required(t("messages.checkRequiredFields")),
    }),
    localPort: Yup.string().when("supplier", {
      is: (supplier) =>
        supplier === "dotypay" || supplier === "globalPayments",
      then: (schema) => schema.notRequired(),
      otherwise: (schema) =>
        schema.required(t("messages.checkRequiredFields")),
    }),
    serverPort: Yup.string().when("supplier", {
      is: (supplier) =>
        supplier === "dotypay" || supplier === "globalPayments",
      then: (schema) => schema.notRequired(),
      otherwise: (schema) =>
        schema.required(t("messages.checkRequiredFields")),
    }),
  }),
  ekasa: Yup.object().shape({
    headerText: Yup.string(),
    footerText: Yup.string(),
    // SK Portos polia sú povinné pre SK eKasu; pre českú EET (eet_cz) sa nevyžadujú.
    dic: Yup.string().when("eKasaType", {
      is: "eet_cz",
      then: (schema) => schema.notRequired(),
      otherwise: (schema) => schema.required(t("messages.checkRequiredFields")),
    }),
    cashRegisterCode: Yup.string().when("eKasaType", {
      is: "eet_cz",
      then: (schema) => schema.notRequired(),
      otherwise: (schema) => schema.required(t("messages.checkRequiredFields")),
    }),
    customer: Yup.object().shape({
      id: Yup.string().when("eKasaType", {
        is: "eet_cz",
        then: (schema) => schema.notRequired(),
        otherwise: (schema) => schema.required(t("messages.checkRequiredFields")),
      }),
      type: Yup.string().when("eKasaType", {
        is: "eet_cz",
        then: (schema) => schema.notRequired(),
        otherwise: (schema) => schema.required(t("messages.checkRequiredFields")),
      }),
    }),
    print: Yup.object().shape({
      options: Yup.object().shape({
        to: Yup.string().when("eKasaType", {
          is: "eet_cz",
          then: (schema) => schema.notRequired(),
          otherwise: (schema) =>
            schema.required(t("messages.checkRequiredFields")),
        }),
      }),
      printerName: Yup.string().when("eKasaType", {
        is: "eet_cz",
        then: (schema) => schema.notRequired(),
        otherwise: (schema) => schema.required(t("messages.checkRequiredFields")),
      }),
    }),
    transactionType: Yup.string().when("eKasaType", {
      is: "eet_cz",
      then: (schema) => schema.notRequired(),
      otherwise: (schema) => schema.required(t("messages.checkRequiredFields")),
    }),
    eKasaType: Yup.string().required(t("messages.checkRequiredFields")),
    // EET polia sú povinné len pre eet_cz (eet je súrodenec eKasaType).
    eet: Yup.object().when("eKasaType", {
      is: "eet_cz",
      then: (schema) =>
        schema.shape({
          eicPopl: Yup.string().required(t("messages.checkRequiredFields")),
          idJednotky: Yup.string().required(t("messages.checkRequiredFields")),
          idPokl: Yup.string()
            .max(20, t("messages.eetIdPoklMax"))
            .required(t("messages.checkRequiredFields")),
          certRef: Yup.string().required(t("messages.checkRequiredFields")),
        }),
      otherwise: (schema) => schema.shape({}),
    }),
  }),
  location: Yup.string().required(t("messages.checkRequiredFields")),
});

const DevicesField = ({
  handleChange,
  expanded,
  isAdmin,
  isPartnerAdmin,
  token,
  customerUrl,
  isPartner,
  isCustomer = false,
  hideSectionTitle = false,
  /** When set (e.g. customer-detail Devices page), workspace dropdown uses POS Workspaces API for this customer id. */
  posWorkspacesCustomerId,
  /** Edited customer's BE domain for correct prod/staging POS workspace API routing. */
  posWorkspacesBeDomain,
}) => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext();
  const [selectedDevice, setSelectedDevice] = useState("1");
  const [innerTabs, setInnerTabs] = useState({});
  const [manuelChecking, setManuelChecking] = useState(false);
  const { showDialog, hideDialog } = useJumboDialog();
  const { enqueueSnackbar } = useSnackbar();

  // Full device list from form values; no role-based filtering (Partner and Service Partner see all customer devices).
  const deviceKeys = Object.keys(values.customer.devices || {});

  const handleManualCheckDevice = async (deviceId) => {
    setManuelChecking(true);
    const handler = isPartnerAdmin ? getPartnerDeviceStatus : getDeviceStatus;
    const status = await handler(deviceId, customerUrl, token);

    const ekasaMessage = `${t("pages.dsokDetails.ekasa")}: ${
      status.ekasaStatus
        ? t("pages.dsokDetails.deviceOK")
        : t("pages.dsokDetails.deviceUnavailable")
    }`;

    const posMessage = `${t("pages.dsokDetails.pos")}: ${
      status.posStatus
        ? t("pages.dsokDetails.deviceOK")
        : t("pages.dsokDetails.deviceUnavailable")
    }`;
    enqueueSnackbar(ekasaMessage, {
      variant: status.ekasaStatus ? "success" : "error",
    });
    enqueueSnackbar(posMessage, {
      variant: status.posStatus ? "success" : "error",
    });
    setManuelChecking(false);
  };

  const handleAddDevice = () => {
    const posPort =
      DSOK_DEVICE_SERVER_PORT_START + (+deviceKeys.length - 1) * 16;
    const ekasaPort = posPort + 1;
    const generalServerPort = ekasaPort + 1;
    setFieldValue("customer.devices", {
      ...values.customer.devices,
      [deviceKeys.length + 1]: {
        uuid: generateUuidV4(),
        connectionCode: "",
        deviceType: "kiosk",
        workspaceId: "",
        location: "1",
        pos: { supplier: DSOK_POS_SUPPLIERS[0].code, serverPort: posPort },
        ekasa: {
          print: { options: { to: "" } },
          serverPort: ekasaPort,
        },
        generalServer: { serverPort: generalServerPort },
      },
    });
  };

  const handleDeleteDevice = (device) => {
    const newDevices = {};
    let deleted = 0;
    deviceKeys.forEach((key, index) => {
      if (key === device) {
        deleted++;
        return;
      }
      newDevices[index + 1 - deleted] = values.customer.devices[key];
    });
    setFieldValue("customer.devices", newDevices);
    setSelectedDevice(deviceKeys[0]);
    hideDialog();
  };

  const content = (
    <Stack
          direction="row"
          alignItems="center"
          width="100%"
          spacing={1}
          px={2}
        >
          <TabContext value={selectedDevice}>
            <Stack width="100%">
              <Box
                sx={{
                  borderBottom: 1,
                  borderColor: "divider",
                }}
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
              >
                <TabList onChange={(_, val) => setSelectedDevice(val)}>
                  {deviceKeys.map((item) => {
                    let demoDeviceText = "";
                    if (values.customer.devices[item].demoMode) {
                      demoDeviceText = " - DEMO";
                    }
                    return (
                      <Tab
                        key={`tab_device_${item}`}
                        label={
                          <Stack direction="row" sx={{ alignItems: "center" }}>
                            {`${item} ` + demoDeviceText}
                            {isAdmin && (
                              <IconButton
                                onClick={() =>
                                  showDialog({
                                    variant: "confirm",
                                    title: t(
                                      "widgets.confirmDialog.areYouSure"
                                    ),
                                    onYes: () => handleDeleteDevice(item),
                                    onNo: () => hideDialog(),
                                  })
                                }
                              >
                                <ClearIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Stack>
                        }
                        value={item}
                      />
                    );
                  })}
                </TabList>
                {isAdmin && (
                  <IconButton onClick={handleAddDevice}>
                    <AddCircleIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
              {!deviceKeys?.length ? (
                <JumboListNoDataPlaceholder />
              ) : (
                deviceKeys.map((device) => {
                  let isFrontendDemoDevice = false;
                  if (values.customer.devices[device].demoMode) {
                    isFrontendDemoDevice = true;
                  }
                  const customerId = values.customer.customerId;
                  const customerCode = values.customer.code;
                  const locationId = values.customer.devices[device].location;
                  const locationCode =
                    values.customer.locations[locationId].code;
                  const deviceUUID = values.customer.devices[device].uuid;
                  // DSOK-442: zariadenie typu "external" nemá POS/eKasu, ale vlastný tok objednávky.
                  const isExternalDeviceRow =
                    String(
                      values.customer.devices[device].deviceType ?? "kiosk",
                    ).toLowerCase() === "external";

                  let env = values?.customer?.beDomain?.includes("staging")
                    ? "stage"
                    : "prod";
                  const isLocalhost =
                    window.location.hostname.includes("localhost");
                  if (isLocalhost) {
                    env = "dev";
                  }
                  //compose preview link based on selected frontend domain
                  let previewButtonLink = `https://${replaceAll(
                    values.customer.code,
                    "-",
                    ""
                  )}.${DSOK_FE_DOMAIN}/${
                    values.customer?.defaultLanguage
                  }/?kioskId=${values.customer.devices[device]?.uuid}`;
                  if (values.customer?.defaultLanguage === "sk") {
                    previewButtonLink = `https://${replaceAll(
                      values.customer.code,
                      "-",
                      ""
                    )}.${DSOK_FE_DOMAIN}/?kioskId=${
                      values.customer.devices[device]?.uuid
                    }`;
                  }

                  let callScreenUrl = null;
                  let orderStatusScreenUrl = `${window.location.origin}/order-status-screen/${customerCode}/${locationCode}/${env}/sk-SK/15000/restaurant`;

                  switch (values?.customer?.frontendType) {
                    case "easyPay":
                      previewButtonLink = `https://easypay.gastrokiosk.online/?customerId=${customerId}&deviceId=${deviceUUID}`;
                      callScreenUrl = `${window.location.origin}/easy-pay/call-screen/${customerCode}/${locationCode}/${env}/sk-SK/15000`;
                      break;
                    case "smartKiosk":
                    case "smartKioskStaging": // legacy, same as smartKiosk (preview by beDomain)
                      previewButtonLink =
                        values.customer.beDomain?.includes("staging")
                          ? `https://dsok-staging.smartkiosk.sk/?customerId=${customerId}&deviceId=${deviceUUID}`
                          : `https://dsok.smartkiosk.sk/?customerId=${customerId}&deviceId=${deviceUUID}`;
                      break;
                    case "easyFood":
                      if (values.customer.beDomain.includes("staging")) {
                        previewButtonLink = `https://easyfood-staging.smartkiosk.sk/?customerId=${customerId}&deviceId=${deviceUUID}`;
                      } else {
                        previewButtonLink = `https://easyfood.smartkiosk.sk/?customerId=${customerId}&deviceId=${deviceUUID}`;
                      }
                      break;
                    default:
                      break;
                  }
                  const posSupplier =
                    values.customer.devices[device].pos?.supplier;
                  const posEkasaEnabled =
                    values.customer.devices[device].pos?.ekasa;
                  const innerTab = innerTabs[device] || "general";
                  return (
                    <TabPanel
                      key={`tab_panel_device_${device}`}
                      value={device}
                      sx={{
                        pb: 1,
                        pr: 0,
                        pl: 0,
                      }}
                    >
                      <Grid container spacing={1}>
                        <Grid item xs={12}>
                          <Stack
                            direction="row"
                            sx={{ alignItems: "stretch", width: "100%" }}
                          >
                            <TabContext value={innerTab}>
                              <Box
                                sx={{
                                  borderRight: 1,
                                  borderColor: "divider",
                                  minWidth: 200,
                                  display: "flex",
                                  flexDirection: "column",
                                }}
                              >
                                <TabList
                                  orientation="vertical"
                                  onChange={(_, val) =>
                                    setInnerTabs((prev) => ({
                                      ...prev,
                                      [device]: val,
                                    }))
                                  }
                                  sx={{
                                    flex: 1,
                                    "& .MuiTabList-flexContainer": {
                                      flexDirection: "column",
                                      alignItems: "stretch",
                                    },
                                  }}
                                >
                                  <Tab
                                    label={t("pages.dsokDetails.generalTab")}
                                    value="general"
                                    sx={{
                                      textTransform: "none",
                                      alignItems: "flex-start",
                                    }}
                                  />
                                  <Tab
                                    label={t("pages.dsokDetails.devicesTab")}
                                    value="devices"
                                    sx={{
                                      textTransform: "none",
                                      alignItems: "flex-start",
                                    }}
                                  />
                                </TabList>
                              </Box>
                              <Box sx={{ flex: 1, minWidth: 0, pl: 2 }}>
                                <TabPanel value="general" sx={{ p: 0 }}>
                                  <Grid container spacing={1}>
                                    <GeneralSection
                                      device={device}
                                      setFieldValue={setFieldValue}
                                      values={values}
                                      t={t}
                                      isAdmin={isAdmin}
                                      isPartnerAdmin={isPartnerAdmin}
                                      isPartner={isPartner}
                                      isCustomer={isCustomer}
                                      showConnectionCode={
                                        values.customer.communicationType ===
                                        "DLA"
                                      }
                                      previewButtonLink={previewButtonLink}
                                      callScreenUrl={callScreenUrl}
                                      orderStatusScreenUrl={
                                        orderStatusScreenUrl
                                      }
                                      isFrontendDemoDevice={
                                        isFrontendDemoDevice
                                      }
                                      manuelChecking={manuelChecking}
                                      handleManualCheckDevice={
                                        handleManualCheckDevice
                                      }
                                      posWorkspacesCustomerId={
                                        posWorkspacesCustomerId
                                      }
                                      posWorkspacesBeDomain={
                                        posWorkspacesBeDomain
                                      }
                                    />
                                  </Grid>
                                </TabPanel>
                                <TabPanel value="devices" sx={{ p: 0 }}>
                                  <Grid container spacing={1}>
                                    {isAdmin && (
                                      <>
                                        {isExternalDeviceRow ? (
                                          <ExternalDeviceSection
                                            device={device}
                                            setFieldValue={setFieldValue}
                                            values={values}
                                            t={t}
                                          />
                                        ) : (
                                          <>
                                            <PosSection
                                              device={device}
                                              setFieldValue={setFieldValue}
                                              values={values}
                                              t={t}
                                              posSupplier={posSupplier}
                                            />
                                            {!posEkasaEnabled && (
                                              <EkasaSection
                                                device={device}
                                                setFieldValue={setFieldValue}
                                                values={values}
                                                t={t}
                                                posSupplier={posSupplier}
                                              />
                                            )}
                                          </>
                                        )}
                                        {!isExternalDeviceRow && (
                                          <>
                                        <Grid item xs={12} md={12} lg={12}>
                                          <Typography
                                            sx={{
                                              width: "33%",
                                              flexShrink: 0,
                                              mt: 3,
                                            }}
                                          >
                                            {t(
                                              "pages.dsokDetails.generalServer",
                                            )}
                                          </Typography>
                                          <Divider />
                                        </Grid>
                                        <Grid item xs={12} md={6} lg={4}>
                                          <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
                                            <FormControlLabel
                                              control={
                                                <Field
                                                  name={`customer.devices.${device}.generalServer.enableGeneralServer`}
                                                  component={Switch}
                                                  onChange={(e) =>
                                                    setFieldValue(
                                                      `customer.devices.${device}.generalServer.enableGeneralServer`,
                                                      e.target.checked,
                                                    )
                                                  }
                                                />
                                              }
                                              label={
                                                <Label
                                                  text={t(
                                                    "pages.dsokDetails.enableGeneralServer",
                                                  )}
                                                  color="primary"
                                                  helpText={t(
                                                    "pages.dsokDetails.enableGeneralServerHelp",
                                                  )}
                                                />
                                              }
                                              checked={
                                                values.customer.devices[device]
                                                  .generalServer
                                                  ?.enableGeneralServer
                                              }
                                            />
                                          </Div>
                                        </Grid>
                                        <Grid item xs={12} md={3} lg={4}>
                                          <Div sx={{ mt: 1, mb: 1 }}>
                                            <JumboTextField
                                              fullWidth
                                              name={`customer.devices.${device}.generalServer.localPort`}
                                              label={t(
                                                "pages.dsokDetails.localPort",
                                              )}
                                            />
                                          </Div>
                                        </Grid>
                                        <Grid item xs={12} md={3} lg={4}>
                                          <Div sx={{ mt: 1, mb: 1 }}>
                                            <JumboTextField
                                              fullWidth
                                              InputProps={{
                                                readOnly: true,
                                              }}
                                              name={`customer.devices.${device}.generalServer.serverPort`}
                                              label={t(
                                                "pages.dsokDetails.serverPort",
                                              )}
                                            />
                                          </Div>
                                        </Grid>
                                          </>
                                        )}
                                      </>
                                    )}
                                  </Grid>
                                </TabPanel>
                              </Box>
                            </TabContext>
                          </Stack>
                        </Grid>
                      </Grid>
                    </TabPanel>
                  );
                })
              )}
            </Stack>
          </TabContext>
        </Stack>
  );

  if (hideSectionTitle) {
    return content;
  }

  return (
    <Accordion
      expanded={expanded === FIELD_NAME}
      onChange={handleChange(FIELD_NAME)}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography sx={{ width: "33%", flexShrink: 0 }}>
          {t("pages.dsokDetails.devices")}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>{content}</AccordionDetails>
    </Accordion>
  );
};

export default DevicesField;
